<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import Code from 'dashboard/components/Code.vue';
import { INSTALLATION_TARGETS } from 'dashboard/helper/installationSnippets';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const dialogRef = ref(null);
const activeTabIndex = ref(0);

const webWidgetScript = computed(() => props.inbox.web_widget_script || '');
const websiteToken = computed(() => props.inbox.website_token || '');

const baseUrl = computed(() => {
  const [, parsedUrl] =
    webWidgetScript.value.match(/BASE_URL\s*=\s*"([^"]*)"/) || [];
  return parsedUrl || window.location.origin;
});

const tabs = computed(() =>
  INSTALLATION_TARGETS.map(target => ({
    key: target.key,
    label: t(`INBOX_MGMT.INSTALL_KIT.TARGETS.${target.key.toUpperCase()}.TAB`),
  }))
);

const activeTarget = computed(() => INSTALLATION_TARGETS[activeTabIndex.value]);

const instruction = computed(() =>
  t(
    `INBOX_MGMT.INSTALL_KIT.TARGETS.${activeTarget.value.key.toUpperCase()}.INSTRUCTION`
  )
);

const snippet = computed(() =>
  activeTarget.value.generate({
    baseUrl: baseUrl.value,
    websiteToken: websiteToken.value,
    webWidgetScript: webWidgetScript.value,
  })
);

const handleTabChange = tab => {
  activeTabIndex.value = tabs.value.findIndex(item => item.key === tab.key);
};

const open = () => {
  activeTabIndex.value = 0;
  dialogRef.value?.open();
};

const close = () => dialogRef.value?.close();

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="3xl"
    :title="t('INBOX_MGMT.INSTALL_KIT.TITLE')"
    :description="t('INBOX_MGMT.INSTALL_KIT.SUBTITLE')"
  >
    <div class="flex flex-col gap-4">
      <div class="overflow-x-auto">
        <TabBar
          :tabs="tabs"
          :initial-active-tab="activeTabIndex"
          @tab-changed="handleTabChange"
        />
      </div>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ instruction }}
      </p>
      <div
        class="p-3 overflow-auto rounded-lg bg-n-slate-2 max-h-96 [&_code]:!bg-n-slate-2 [&_pre]:whitespace-pre-wrap [&_pre]:break-words [&_code]:whitespace-pre-wrap"
      >
        <Code :script="snippet" :lang="activeTarget.lang" />
      </div>
    </div>
    <template #footer>
      <div class="flex justify-end w-full">
        <NextButton
          type="button"
          :label="t('INBOX_MGMT.INSTALL_KIT.DONE')"
          @click="close"
        />
      </div>
    </template>
  </Dialog>
</template>
